import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Save, Loader2, Settings as SettingsIcon, Users, Columns3,
  Plus, Trash2, ArrowUp, ArrowDown,
} from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Toggle } from '@/components/ui/Toggle'
import { NumberInput } from '@/components/ui/NumberInput'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { toast } from '@/components/ui/Toast'
import { cn } from '@/lib/cn'
import { useAuthority } from '@/hooks/useAuthority'
import {
  ReportTemplatesService,
  type ColumnSourceType,
  type FieldCatalog,
  type ReportTemplatePayload,
} from '@/services/modules/ReportTemplatesService'
import { ServiceProvidersService, type ServiceProvider } from '@/services/modules/ServiceProvidersService'
import { DegreeRelationshipsService, type DegreeRelationship } from '@/services/modules/DegreeRelationshipsService'

const LIST_PATH = '/settings/commercial/report-templates'

const SOURCE_TYPE_LABELS: Record<ColumnSourceType, string> = {
  FIELD: 'Campo do sistema',
  CONSTANT: 'Valor fixo',
  BLANK: 'Vazio (em branco)',
}
const FORMAT_LABELS: Record<string, string> = {
  raw: 'Sem formatação',
  cpf: 'CPF (000.000.000-00)',
  date: 'Data (dd/mm/aaaa)',
  cep: 'CEP (00000-000)',
  phone: 'Telefone',
  upper: 'MAIÚSCULAS',
}

interface DraftColumn {
  uid: string
  label: string
  sourceType: ColumnSourceType
  sourceKey: string
  constantValue: string
  format: string // 'raw' = sem formatação
}
interface FamilySel { checked: boolean; applyAgeLimit: boolean }

function uid() { return crypto.randomUUID() }

/**
 * /settings/commercial/report-templates/(novo|:id/editar)
 *
 * Construtor da ESTRUTURA de relatório: prestadores que a compartilham, formato,
 * regra de família (graus que ficam como dependente + idade dos filhos) e as
 * colunas ordenadas. A GERAÇÃO do arquivo fica na tela de Relatórios de
 * Provedores (/commercial/reports/providers), não aqui.
 */
export function SettingsReportTemplateFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  // POST /settings/report-templates → create; PUT /settings/report-templates/{id} → edit.
  const { can } = useAuthority()
  const canSave = can(isEdit ? 'settings.report_templates.edit' : 'settings.report_templates.create')

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeSection, setActiveSection] = useState('basic')

  // Catálogos
  const [catalog, setCatalog] = useState<FieldCatalog>({ fields: [], formats: [], builtins: [] })
  const [providers, setProviders] = useState<ServiceProvider[]>([])
  const [degrees, setDegrees] = useState<DegreeRelationship[]>([])

  // Estado do formulário
  const [name, setName] = useState('')
  const [format, setFormat] = useState<'xlsx' | 'csv'>('xlsx')
  const [builtinKey, setBuiltinKey] = useState('') // '' = personalizado
  const [childAgeLimit, setChildAgeLimit] = useState<number | null>(18)
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [providerIds, setProviderIds] = useState<string[]>([])
  const [family, setFamily] = useState<Record<string, FamilySel>>({})
  const [columns, setColumns] = useState<DraftColumn[]>([])

  const isBuiltin = builtinKey !== ''

  // ----- Carga inicial (loading já inicia true) -----
  useEffect(() => {
    let alive = true
    Promise.all([
      ReportTemplatesService.fieldCatalog().catch(() => ({ fields: [], formats: [], builtins: [] } as FieldCatalog)),
      ServiceProvidersService.list({ size: 500, status: 'ATIVO' }).then(r => r.data).catch(() => []),
      DegreeRelationshipsService.list({ size: 500, status: 'ATIVO' }).then(r => r.data).catch(() => []),
      isEdit && id ? ReportTemplatesService.show(id) : Promise.resolve(null),
    ]).then(([cat, provs, degs, tpl]) => {
      if (!alive) return
      setCatalog(cat)
      setProviders(provs)
      setDegrees(degs)
      if (tpl) {
        setName(tpl.name)
        setFormat(tpl.format)
        setBuiltinKey(tpl.builtinKey ?? '')
        setChildAgeLimit(tpl.childAgeLimit ?? 18)
        setStatus(tpl.status)
        setProviderIds(tpl.serviceProviderIds ?? [])
        const fam: Record<string, FamilySel> = {}
        for (const fd of tpl.familyDegrees ?? []) {
          fam[fd.degreeRelationshipId] = { checked: true, applyAgeLimit: fd.applyAgeLimit }
        }
        setFamily(fam)
        setColumns((tpl.columns ?? []).map(c => ({
          uid: uid(),
          label: c.label,
          sourceType: c.sourceType,
          sourceKey: c.sourceKey ?? '',
          constantValue: c.constantValue ?? '',
          format: c.format ?? 'raw',
        })))
      }
    }).finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [isEdit, id])

  // ----- Colunas: helpers -----
  function addColumn() {
    setColumns(prev => [...prev, { uid: uid(), label: '', sourceType: 'FIELD', sourceKey: '', constantValue: '', format: 'raw' }])
  }
  function updateColumn(u: string, patch: Partial<DraftColumn>) {
    setColumns(prev => prev.map(c => c.uid === u ? { ...c, ...patch } : c))
  }
  function removeColumn(u: string) {
    setColumns(prev => prev.filter(c => c.uid !== u))
  }
  function moveColumn(u: string, dir: -1 | 1) {
    setColumns(prev => {
      const i = prev.findIndex(c => c.uid === u)
      const j = i + dir
      if (i < 0 || j < 0 || j >= prev.length) return prev
      const next = [...prev]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  }

  // ----- Família: helpers -----
  function toggleDegree(degreeId: string, checked: boolean) {
    setFamily(prev => ({ ...prev, [degreeId]: { checked, applyAgeLimit: prev[degreeId]?.applyAgeLimit ?? false } }))
  }
  function toggleDegreeAge(degreeId: string, applyAgeLimit: boolean) {
    setFamily(prev => ({ ...prev, [degreeId]: { checked: prev[degreeId]?.checked ?? true, applyAgeLimit } }))
  }

  // ----- Validação por seção -----
  const errors = useMemo(() => {
    const e: { basic: string[]; columns: string[] } = { basic: [], columns: [] }
    if (!name.trim()) e.basic.push('Informe o nome da estrutura.')
    // Colunas/regra de família só se aplicam à estrutura personalizada.
    if (!isBuiltin) {
      if (columns.length === 0) e.columns.push('Adicione ao menos uma coluna.')
      columns.forEach((c, i) => {
        if (!c.label.trim()) e.columns.push(`Coluna ${i + 1}: informe o rótulo.`)
        if (c.sourceType === 'FIELD' && !c.sourceKey) e.columns.push(`Coluna ${i + 1}: escolha o campo de origem.`)
        if (c.sourceType === 'CONSTANT' && !c.constantValue.trim()) e.columns.push(`Coluna ${i + 1}: informe o valor fixo.`)
      })
    }
    return e
  }, [name, columns, isBuiltin])

  function buildPayload(): ReportTemplatePayload {
    const base: ReportTemplatePayload = {
      name: name.trim(),
      builtinKey: isBuiltin ? builtinKey : null,
      status,
      serviceProviderIds: providerIds,
    }
    if (isBuiltin) {
      // Estrutura embutida: layout/formato definidos em código. Limpa config personalizada.
      return { ...base, columns: [], familyDegrees: [] }
    }
    const familyDegrees = Object.entries(family)
      .filter(([, v]) => v.checked)
      .map(([degreeRelationshipId, v]) => ({ degreeRelationshipId, applyAgeLimit: v.applyAgeLimit }))
    return {
      ...base,
      format,
      childAgeLimit: childAgeLimit ?? 18,
      columns: columns.map(c => ({
        label: c.label.trim(),
        sourceType: c.sourceType,
        sourceKey: c.sourceType === 'FIELD' ? c.sourceKey : null,
        constantValue: c.sourceType === 'CONSTANT' ? c.constantValue.trim() : null,
        format: c.sourceType === 'FIELD' && c.format !== 'raw' ? c.format : null,
      })),
      familyDegrees,
    }
  }

  async function handleSave(): Promise<void> {
    if (errors.basic.length || errors.columns.length) {
      setActiveSection(errors.basic.length ? 'basic' : 'columns')
      toast.error({ title: 'Revise o formulário', description: (errors.basic[0] ?? errors.columns[0]) })
      return
    }
    setSaving(true)
    try {
      const payload = buildPayload()
      if (isEdit && id) {
        await ReportTemplatesService.update(id, payload)
        toast.success('Estrutura atualizada.')
      } else {
        await ReportTemplatesService.create(payload)
        toast.success('Estrutura criada.')
      }
      // Regra global: após criar/atualizar, volta para a listagem.
      navigate(LIST_PATH)
    } catch (e) {
      toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  const typeOptions = [{ value: '', label: 'Personalizado (colunas configuráveis)' }, ...catalog.builtins]

  const sections: FormSection[] = [
    {
      key: 'basic', label: 'Dados da estrutura', icon: SettingsIcon,
      hasError: errors.basic.length > 0, errorCount: errors.basic.length,
      content: (
        <SectionShell title="Dados da estrutura" description="Tipo de relatório, nome e status.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nome da estrutura" required wrapperClassName="md:col-span-2" value={name} onChange={e => setName(e.target.value)} placeholder="Ex.: Zelo — Full 2026" />
            <Select
              label="Tipo de relatório" required wrapperClassName="md:col-span-2"
              value={builtinKey} onChange={e => setBuiltinKey(e.target.value)}
              options={typeOptions}
              searchable={false} clearable={false}
              hint={isBuiltin
                ? 'Layout e formato fixos deste parceiro. As colunas não são editáveis.'
                : 'Você monta as colunas e a regra de família livremente.'}
            />
            {!isBuiltin && (
              <>
                <Select
                  label="Formato de exportação" required
                  value={format} onChange={e => setFormat(e.target.value as 'xlsx' | 'csv')}
                  options={[{ value: 'xlsx', label: 'Excel (.xlsx)' }, { value: 'csv', label: 'CSV (;) — UTF-8' }]}
                  searchable={false} clearable={false}
                />
                <NumberInput label="Idade limite dos filhos" value={childAgeLimit} onChange={setChildAgeLimit} min={0} max={120}
                  hint="Filhos acima desta idade viram um novo titular no arquivo." />
              </>
            )}
            <div className="md:col-span-2"><StatusToggle value={status} onChange={setStatus} /></div>
          </div>
        </SectionShell>
      ),
    },
    {
      key: 'providers', label: 'Prestadores', icon: Users,
      content: (
        <SectionShell title="Prestadores que usam esta estrutura" description="Vários prestadores podem compartilhar a mesma estrutura, e um prestador pode estar em mais de uma. Na exportação, se o prestador tiver mais de um modelo, o usuário escolhe qual usar. Prestador sem nenhum modelo continua no Excel simples atual.">
          {providers.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Nenhum prestador ativo cadastrado.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {providers.map(p => {
                const checked = providerIds.includes(p.id)
                return (
                  <label key={p.id} className={cn('flex items-center gap-2 px-3 py-2 rounded-md ring-1 cursor-pointer transition', checked ? 'ring-brand-300 bg-brand-50' : 'ring-gray-200 hover:bg-gray-50')}>
                    <input type="checkbox" checked={checked} onChange={e => setProviderIds(prev => e.target.checked ? [...prev, p.id] : prev.filter(x => x !== p.id))} className="accent-brand-600" />
                    <span className="text-sm text-gray-800">{p.name}</span>
                  </label>
                )
              })}
            </div>
          )}
        </SectionShell>
      ),
    },
    {
      key: 'family', label: 'Regra de família', icon: Users,
      content: (
        <SectionShell title="Grupo familiar" description="Marque os graus que PERMANECEM como dependente no arquivo. Os demais dependentes viram um novo titular. Ative o limite de idade para graus que respeitam a idade limite (ex.: filhos).">
          {degrees.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Nenhum grau de parentesco ativo cadastrado.</p>
          ) : (
            <div className="space-y-2">
              {degrees.map(d => {
                const sel = family[d.id]
                const checked = !!sel?.checked
                return (
                  <div key={d.id} className={cn('flex items-center justify-between gap-3 px-3 py-2 rounded-md ring-1 transition', checked ? 'ring-brand-300 bg-brand-50' : 'ring-gray-200')}>
                    <label className="flex items-center gap-2 cursor-pointer min-w-0">
                      <input type="checkbox" checked={checked} onChange={e => toggleDegree(d.id, e.target.checked)} className="accent-brand-600" />
                      <span className="text-sm text-gray-800 break-words">{d.degreeRelationship}</span>
                      {d.exportCode && <Badge variant="gray">{d.exportCode}</Badge>}
                    </label>
                    {checked && (
                      <Toggle checked={!!sel?.applyAgeLimit} onChange={v => toggleDegreeAge(d.id, v)} label="Limite de idade" size="sm" labelSize="sm" />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </SectionShell>
      ),
    },
    {
      key: 'columns', label: 'Colunas', icon: Columns3,
      hasError: errors.columns.length > 0, errorCount: errors.columns.length,
      content: (
        <SectionShell title="Colunas do arquivo" description="Defina, na ordem, cada coluna do arquivo exportado.">
          <div className="space-y-3">
            {columns.length === 0 && <p className="text-sm text-gray-400 italic">Nenhuma coluna. Clique em "Adicionar coluna".</p>}
            {columns.map((c, i) => (
              <div key={c.uid} className="rounded-md ring-1 ring-gray-200 p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] font-semibold text-gray-400 w-6">#{i + 1}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <IconBtn label="Mover para cima" onClick={() => moveColumn(c.uid, -1)} disabled={i === 0}><ArrowUp size={14} /></IconBtn>
                    <IconBtn label="Mover para baixo" onClick={() => moveColumn(c.uid, 1)} disabled={i === columns.length - 1}><ArrowDown size={14} /></IconBtn>
                    <IconBtn label="Remover coluna" danger onClick={() => removeColumn(c.uid)}><Trash2 size={14} /></IconBtn>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input label="Rótulo (cabeçalho)" value={c.label} onChange={e => updateColumn(c.uid, { label: e.target.value })} placeholder="Ex.: NOME" />
                  <Select
                    label="Origem" value={c.sourceType}
                    onChange={e => updateColumn(c.uid, { sourceType: e.target.value as ColumnSourceType })}
                    options={(['FIELD', 'CONSTANT', 'BLANK'] as ColumnSourceType[]).map(v => ({ value: v, label: SOURCE_TYPE_LABELS[v] }))}
                    searchable={false} clearable={false}
                  />
                  {c.sourceType === 'FIELD' && (
                    <Select
                      label="Campo do sistema" value={c.sourceKey}
                      onChange={e => updateColumn(c.uid, { sourceKey: e.target.value })}
                      options={catalog.fields.map(f => ({ value: f.key, label: f.label }))}
                      placeholder="Selecione"
                    />
                  )}
                  {c.sourceType === 'CONSTANT' && (
                    <Input label="Valor fixo" value={c.constantValue} onChange={e => updateColumn(c.uid, { constantValue: e.target.value })} placeholder="Texto fixo" />
                  )}
                  {c.sourceType === 'FIELD' && (
                    <Select
                      label="Formatação" value={c.format}
                      onChange={e => updateColumn(c.uid, { format: e.target.value })}
                      options={catalog.formats.map(f => ({ value: f, label: FORMAT_LABELS[f] ?? f }))}
                      searchable={false} clearable={false}
                    />
                  )}
                </div>
              </div>
            ))}
            <Button type="button" variant="ghost" onClick={addColumn} iconLeft={<Plus size={14} />}>Adicionar coluna</Button>
          </div>
        </SectionShell>
      ),
    },
  ].filter(s => !isBuiltin || (s.key !== 'family' && s.key !== 'columns'))

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar estrutura de relatório' : 'Nova estrutura de relatório'}
        subtitle="Modelos de relatório configuráveis por prestador"
        crumbs={[
          { label: 'Configurações', to: '/settings' },
          { label: 'Comercial' },
          { label: 'Modelos de relatório', to: LIST_PATH },
          { label: isEdit ? 'Editar' : 'Novo' },
        ]}
        backTo={LIST_PATH}
        actions={(
          <Button onClick={handleSave} loading={saving} disabled={!canSave} iconLeft={<Save size={14} />}
            title={canSave ? undefined : `Sem permissão para ${isEdit ? 'editar' : 'criar'} modelo de relatório`}>Salvar</Button>
        )}
      />
      <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />
    </>
  )
}

// =============================================================================
// Subcomponentes
// =============================================================================
function SectionShell({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  )
}

function IconBtn({ children, label, onClick, disabled, danger }: { children: React.ReactNode; label: string; onClick: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} disabled={disabled}
      className={cn('inline-flex items-center justify-center w-8 h-8 rounded-md transition disabled:opacity-30 disabled:cursor-not-allowed',
        danger ? 'text-rose-500 hover:bg-rose-50' : 'text-gray-500 hover:bg-gray-100')}>
      {children}
    </button>
  )
}
